/**
 * Simple Notifications Component
 *
 * Minimal interface for learners to view and respond to course invitations
 */

import { Component, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { environment } from '../../environments/environment';

import { IconComponent } from '../shared/components/icon/icon.component';
import { RichTextNoteDisplayComponent } from '../shared/components/rich-text-note-display/rich-text-note-display.component';
import { PaginationComponent } from '../shared/components/pagination/pagination.component';
import { ProfileService } from '../profile/profile.service';
import { AuthService } from '../auth/auth.service';
import { NotificationStateService } from '../shared/services/notification-state.service';
import { NotificationsApiService } from '../shared/services/notifications-api.service';
import { AppNotification } from '../shared/types/notification.types';
import { PaginationService } from '../shared/services/pagination.service';
import { PaymentsCheckoutService } from '../payments/payments-checkout.service';
import { formatCentsToDollars } from '../payments/course-pricing-format';

@Component({
  selector: 'app-simple-notifications',
  standalone: true,
  imports: [CommonModule, DatePipe, RouterLink, IconComponent, PaginationComponent, RichTextNoteDisplayComponent],
  template: `
    <div class="min-h-screen bg-base-200 p-4">
      <div class="max-w-4xl mx-auto">
        <!-- Header -->
        <div class="card bg-base-100 shadow-lg mb-6">
          <div class="card-body">
            <h1 class="card-title text-3xl">
              <app-icon name="notifications" size="lg" class="text-primary"></app-icon>
              Invitations
            </h1>
            <p class="text-base-content opacity-70">
              Manage your course and organization invitations
            </p>
          </div>
        </div>

        <!-- Loading -->
        <div *ngIf="isLoading" class="flex justify-center py-16">
          <span class="loading loading-spinner loading-lg"></span>
        </div>

        <!-- Organization Invitations -->
        <div class="space-y-3 mb-6" *ngIf="!isLoading && organizationInvitations.length > 0">
          <h2 class="text-2xl font-bold text-base-content mb-4">
            <app-icon name="business" size="md" class="inline-block mr-2"></app-icon>
            Organization Invitations
          </h2>
          <div *ngFor="let invitation of organizationInvitations"
               class="card bg-base-100 shadow-lg">
            <div class="card-body p-4">
              <div class="flex gap-4">
                <!-- Icon column from sm up. Below sm the same icon renders in the
                     badge row above the name instead, so the name keeps the full
                     card width (KAN-717). -->
                <div class="hidden sm:block flex-shrink-0" aria-hidden="true" data-testid="org-invitation-icon">
                  <div class="w-14 h-14 rounded-full bg-accent flex items-center justify-center">
                    <app-icon name="business" class="text-accent-content"></app-icon>
                  </div>
                </div>

                <!-- Content -->
                <div class="flex-1 min-w-0">
                  <!-- KAN-717: below sm the icon + badge row stacks above the title
                       so a long name gets the full content width while the icon
                       stays visible; from sm up it is the side-by-side row it
                       always was (the icon shows in the column on the left). -->
                  <div class="flex flex-col-reverse items-start gap-2 mb-1 sm:flex-row sm:justify-between sm:gap-2"
                       data-testid="org-invitation-header">
                    <h3 class="font-bold text-lg user-text-wrap" data-testid="org-invitation-title">{{ invitation.organizationName }}</h3>
                    <div class="flex items-center gap-3 flex-shrink-0" data-testid="org-invitation-meta">
                      <div class="sm:hidden flex-shrink-0" aria-hidden="true" data-testid="org-invitation-icon-mobile">
                        <div class="w-10 h-10 rounded-full bg-accent flex items-center justify-center">
                          <app-icon name="business" class="text-accent-content"></app-icon>
                        </div>
                      </div>
                      <span class="badge badge-accent flex-shrink-0" data-testid="org-invitation-badge">Organization</span>
                    </div>
                  </div>

                  <p class="text-sm text-base-content opacity-70 mb-2">
                    From {{ invitation.instructorName }} • Role: {{ getRoleDisplay(invitation.role) }}
                  </p>

                  <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
                       data-testid="org-invitation-actions-row">
                    <div *ngIf="invitation.personalNote || invitation.message" class="border-l-4 border-info pl-3 py-1 min-w-0 sm:flex-1">
                      <p class="text-sm font-semibold">Message:</p>
                      <app-rich-text-note-display class="text-sm text-base-content opacity-80"
                        [value]="invitation.personalNote || invitation.message"></app-rich-text-note-display>
                    </div>
                    <!-- Actions -->
                    <div class="flex flex-wrap gap-2 sm:flex-shrink-0" data-testid="org-invitation-actions">
                      <button
                        class="btn btn-outline btn-error btn-sm"
                        (click)="respondToOrgInvitation(invitation, 'reject')"
                        [disabled]="responding === invitation.id">
                        Decline
                      </button>
                      <button
                        class="btn btn-primary btn-sm"
                        (click)="respondToOrgInvitation(invitation, 'accept')"
                        [disabled]="responding === invitation.id"
                        [class.loading]="responding === invitation.id">
                        Accept & Join
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <app-pagination *ngIf="orgTotal > pageSize"
                          [totalItems]="orgTotal"
                          [currentPage]="orgPage"
                          [pageSize]="pageSize"
                          [showPageSize]="false"
                          (pageChange)="onOrgPageChange($event)"></app-pagination>
        </div>

        <!-- Course Invitations -->
        <div class="space-y-3" *ngIf="!isLoading && invitations.length > 0">
          <h2 class="text-2xl font-bold text-base-content mb-4">
            <app-icon name="school" size="md" class="inline-block mr-2"></app-icon>
            Course Invitations
          </h2>
          <div *ngFor="let invitation of invitations"
               class="card bg-base-100 shadow-lg overflow-hidden">
            <div class="grid grid-cols-1 md:grid-cols-5">
              <!-- Left: Organization panel -->
              <div class="md:col-span-2 bg-base-200 p-4 md:border-r border-base-300 flex flex-col justify-between gap-3">
                <div>
                  <div class="text-[10px] uppercase tracking-wider opacity-60 font-semibold mb-2">Organization</div>
                  <div class="flex items-center gap-2.5 mb-3">
                    <div class="w-10 h-10 rounded-lg bg-accent flex items-center justify-center flex-shrink-0">
                      <app-icon name="business" class="text-accent-content"></app-icon>
                    </div>
                    <div class="min-w-0">
                      <div class="text-sm font-semibold truncate">{{ invitation.organizationName || 'Organization' }}</div>
                      <div *ngIf="isAlreadyMemberOfOrg(invitation)" class="text-xs opacity-60">Including you</div>
                    </div>
                  </div>
                  <span *ngIf="!isAlreadyMemberOfOrg(invitation)"
                        class="badge badge-outline badge-info gap-1 text-xs">
                    <app-icon name="person_add" size="xs"></app-icon>
                    You'll be added to this org
                  </span>
                  <span *ngIf="isAlreadyMemberOfOrg(invitation)"
                        class="badge badge-outline badge-success gap-1 text-xs">
                    <app-icon name="check" size="xs"></app-icon>
                    Member
                  </span>
                </div>
              </div>

              <!-- Right: Course + actions -->
              <div class="md:col-span-3 p-4 flex flex-col gap-3">
                <div class="flex items-start gap-3">
                  <!-- Thumbnail -->
                  <div class="flex-shrink-0">
                    <div *ngIf="invitation.courseThumbnail" class="w-11 h-11 rounded-full overflow-hidden bg-base-200">
                      <img
                        [src]="invitation.courseThumbnail"
                        [alt]="invitation.courseName"
                        class="w-full h-full object-cover"
                      >
                    </div>
                    <div
                      *ngIf="!invitation.courseThumbnail"
                      class="w-11 h-11 rounded-full bg-secondary flex items-center justify-center"
                    >
                      <span class="text-sm font-bold text-secondary-content">
                        {{ getCourseInitials(invitation.courseName) }}
                      </span>
                    </div>
                  </div>

                  <!-- Course info -->
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                      <h3 class="font-bold text-base truncate">{{ invitation.courseName }}</h3>
                      <span class="badge badge-secondary badge-sm flex-shrink-0">Course</span>
                    </div>
                    <p class="text-xs opacity-70 mt-0.5">From {{ invitation.instructorName }}</p>
                    <p *ngIf="invitation.courseDescription" class="text-xs opacity-70 mt-1 line-clamp-2">
                      {{ invitation.courseDescription }}
                    </p>
                  </div>
                </div>

                <div *ngIf="invitation.personalNote || invitation.message" class="border-l-4 border-info pl-3 py-1">
                  <p class="text-xs font-semibold">Message:</p>
                  <app-rich-text-note-display class="text-xs opacity-80"
                    [value]="invitation.personalNote || invitation.message"></app-rich-text-note-display>
                </div>

                <!-- Actions -->
                <div class="flex gap-2 mt-auto">
                  <button
                    class="btn btn-outline btn-error btn-sm flex-1"
                    (click)="respondToInvitation(invitation, 'reject')"
                    [disabled]="responding === invitation.id">
                    Decline
                  </button>
                  <button
                    class="btn btn-primary btn-sm flex-1"
                    (click)="respondToInvitation(invitation, 'accept')"
                    [disabled]="responding === invitation.id"
                    [class.loading]="responding === invitation.id">
                    Accept & Enroll
                  </button>
                </div>
              </div>
            </div>
          </div>
          <app-pagination *ngIf="courseTotal > pageSize"
                          [totalItems]="courseTotal"
                          [currentPage]="coursePage"
                          [pageSize]="pageSize"
                          [showPageSize]="false"
                          (pageChange)="onCoursePageChange($event)"></app-pagination>
        </div>

        <!-- Notifications (KAN-330) -->
        <div class="space-y-3 mb-6" *ngIf="!isLoading && notifications.length > 0">
          <!-- KAN-717: the icon and the word are one flex unit so the bell never
               sits alone above "Notifications" on phones; "Mark all read"
               wraps below when the row runs out of space. -->
          <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-4" data-testid="notifications-header">
            <h2 class="flex items-center text-2xl font-bold text-base-content" data-testid="notifications-heading">
              <app-icon name="notifications" size="md" class="flex-shrink-0 mr-2"></app-icon>
              Notifications
            </h2>
            <button *ngIf="hasUnreadNotifications"
                    class="btn btn-ghost btn-sm"
                    data-testid="mark-all-read"
                    (click)="markAllNotificationsRead()">
              Mark all read
            </button>
          </div>
          <div *ngFor="let n of notifications"
               class="card bg-base-100 shadow-lg"
               [class.opacity-60]="n.isRead"
               data-testid="notification-item">
            <div class="card-body p-4">
              <!-- KAN-717: below sm "Mark read" wraps onto its own full-width
                   line, so the text beside the per-type icon gets the rest of
                   the card width instead of a narrow column. The icon stays
                   visible on phones to match desktop. -->
              <div class="flex flex-wrap sm:flex-nowrap gap-x-4 gap-y-3 items-start" data-testid="notification-row">
                <!-- Per-type discriminator (KAN-330 unit-completed, KAN-331
                     Phase 10 obligation-share, Phase 11 dues-reminder). -->
                <ng-container [ngSwitch]="n.type">
                  <!-- Split-billing member share (KAN-331 Phase 10): "you owe" + Pay CTA -->
                  <ng-container *ngSwitchCase="'obligation_share_due'">
                    <div class="flex-shrink-0" aria-hidden="true" data-testid="notification-icon">
                      <div class="w-11 h-11 rounded-full bg-warning flex items-center justify-center">
                        <app-icon name="payments" class="text-warning-content"></app-icon>
                      </div>
                    </div>
                    <div class="flex-1 min-w-0" data-testid="obligation-share-notification">
                      <div class="flex items-start justify-between gap-2">
                        <p class="font-semibold text-base">
                          You owe {{ formatShareAmount(n) }} toward
                          {{ n.payload?.description || 'a shared bill' }}
                        </p>
                        <span *ngIf="!n.isRead" class="badge badge-primary badge-sm flex-shrink-0">New</span>
                      </div>
                      <p class="text-xs text-base-content opacity-50 mt-1">
                        {{ n.createdAt | date:'medium' }}
                      </p>
                      <div class="mt-3">
                        <button class="btn btn-primary btn-sm"
                                data-testid="pay-obligation-share"
                                [disabled]="payingShareId === n.payload?.shareId"
                                [class.loading]="payingShareId === n.payload?.shareId"
                                (click)="payObligationShare(n)">
                          Pay {{ formatShareAmount(n) }}
                        </button>
                      </div>
                    </div>
                  </ng-container>

                  <!-- Dues reminder (KAN-331 Phase 11 task 11.6c): upcoming-renewal
                       nudge or lapsed-membership notice + a CTA to the member /dues
                       page. Payload is fully denormalized by
                       buildDuesReminderNotificationPayload (no join needed). -->
                  <ng-container *ngSwitchCase="'dues_reminder'">
                    <div class="flex-shrink-0" aria-hidden="true" data-testid="notification-icon">
                      <div class="w-11 h-11 rounded-full flex items-center justify-center"
                           [ngClass]="isDuesReminderLapsed(n) ? 'bg-warning' : 'bg-info'">
                        <app-icon name="payments"
                                  [ngClass]="isDuesReminderLapsed(n) ? 'text-warning-content' : 'text-info-content'"></app-icon>
                      </div>
                    </div>
                    <div class="flex-1 min-w-0" data-testid="dues-reminder-notification">
                      <div class="flex items-start justify-between gap-2">
                        <p class="font-semibold text-base">{{ duesReminderHeadline(n) }}</p>
                        <span *ngIf="!n.isRead" class="badge badge-primary badge-sm flex-shrink-0">New</span>
                      </div>
                      <p *ngIf="isDuesReminderLapsed(n)" class="text-sm text-base-content opacity-70">
                        Access is paused - your progress is saved.
                      </p>
                      <p *ngIf="n.payload?.amountLabel" class="text-sm text-base-content opacity-70">
                        {{ n.payload?.amountLabel }}
                      </p>
                      <p class="text-xs text-base-content opacity-50 mt-1">
                        {{ n.createdAt | date:'medium' }}
                      </p>
                      <div class="mt-3">
                        <a routerLink="/dues"
                           class="btn btn-primary btn-sm"
                           data-testid="dues-reminder-cta">
                          {{ duesReminderCtaLabel(n) }}
                        </a>
                      </div>
                    </div>
                  </ng-container>

                  <!-- Unit completion (KAN-330) -->
                  <ng-container *ngSwitchDefault>
                    <div class="flex-shrink-0" aria-hidden="true" data-testid="notification-icon">
                      <div class="w-11 h-11 rounded-full bg-success flex items-center justify-center">
                        <app-icon name="check_circle" class="text-success-content"></app-icon>
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-start justify-between gap-2">
                        <p class="font-semibold text-base">
                          {{ n.payload?.learnerName || 'A learner' }} completed
                          {{ n.payload?.unitTitle || 'a unit' }}
                        </p>
                        <span *ngIf="!n.isRead" class="badge badge-primary badge-sm flex-shrink-0">New</span>
                      </div>
                      <p class="text-sm text-base-content opacity-70">
                        in {{ n.payload?.courseTitle || 'a course' }}
                      </p>
                      <p class="text-xs text-base-content opacity-50 mt-1">
                        {{ (n.payload?.completedAt || n.createdAt) | date:'medium' }}
                      </p>
                    </div>
                  </ng-container>
                </ng-container>

                <button *ngIf="!n.isRead"
                        class="btn btn-outline btn-sm flex-shrink-0 basis-full sm:basis-auto"
                        data-testid="mark-read"
                        (click)="markNotificationRead(n)">
                  Mark read
                </button>
              </div>
            </div>
          </div>
          <app-pagination *ngIf="notifTotal > pageSize"
                          [totalItems]="notifTotal"
                          [currentPage]="notifPage"
                          [pageSize]="pageSize"
                          [showPageSize]="false"
                          (pageChange)="onNotifPageChange($event)"></app-pagination>
        </div>

        <!-- Empty State -->
        <div *ngIf="!isLoading && invitations.length === 0 && organizationInvitations.length === 0 && notifications.length === 0" class="text-center py-16">
          <app-icon name="notifications_none" size="xl" class="text-base-content/30 mb-4"></app-icon>
          <h2 class="text-2xl font-bold mb-4">No Invitations</h2>
          <p class="text-lg text-base-content opacity-70">
            You don't have any pending invitations.
          </p>
        </div>

        <!-- Success Enrollment Card -->
        <div *ngIf="enrolledCourse" class="card bg-base-100 border-2 border-success shadow-2xl">
          <div class="card-body text-center">
            <div class="flex justify-center mb-4">
              <app-icon name="check_circle" size="xl" class="text-success"></app-icon>
            </div>
            <h2 class="card-title text-2xl justify-center mb-2 text-success">Successfully Enrolled!</h2>
            <p class="text-lg mb-4 text-base-content">You're now enrolled in:</p>

            <div class="flex items-center justify-center gap-3 mb-6 min-w-0 max-w-full">
              <!-- Course Thumbnail -->
              <div *ngIf="enrolledCourse.thumbnail" class="w-16 h-16 rounded-full overflow-hidden bg-base-200 flex-shrink-0">
                <img [src]="enrolledCourse.thumbnail" [alt]="enrolledCourse.name" class="w-full h-full object-cover">
              </div>
              <div *ngIf="!enrolledCourse.thumbnail"
                   class="w-16 h-16 rounded-full bg-base-200 flex items-center justify-center flex-shrink-0">
                <span class="text-xl font-bold text-base-content">
                  {{ getCourseInitials(enrolledCourse.name) }}
                </span>
              </div>
              <!-- Course Name (KAN-211: must wrap inside the enrollment card) -->
              <h3 class="enrolled-course-name text-xl font-bold min-w-0 flex-1 text-left"
                  data-testid="enrolled-course-name"
                  [title]="enrolledCourse.name">{{ enrolledCourse.name }}</h3>
            </div>

            <!-- KAN-119: cross-org enrollment notice. The course belongs to a
                 different organization than the one currently being viewed, so it
                 will not appear in My Courses until the learner switches. We name
                 the owning org and prompt a deliberate switch — we intentionally
                 do NOT auto-switch the org context or auto-surface the course. -->
            <div *ngIf="needsOrgSwitch()"
                 class="alert alert-warning text-left mb-4"
                 data-testid="cross-org-switch-notice">
              <app-icon name="warning" size="sm"></app-icon>
              <span>
                This course belongs to
                <strong>{{ enrolledCourseOrgName || 'another organization' }}</strong>,
                which isn't the organization you're viewing right now. Switch to
                <strong>{{ enrolledCourseOrgName || 'that organization' }}</strong>
                to see and access it in My Courses.
              </span>
            </div>

            <div class="card-actions justify-center">
              <button (click)="navigateToMyCourses()" class="btn btn-lg btn-primary" [disabled]="isSwitching">
                <app-icon *ngIf="!isSwitching" name="school" size="sm"></app-icon>
                <span *ngIf="isSwitching" class="loading loading-spinner loading-sm"></span>
                <ng-container *ngIf="needsOrgSwitch(); else sameOrgLabel">
                  Switch to {{ enrolledCourseOrgName || 'new organization' }} and View My Courses
                </ng-container>
                <ng-template #sameOrgLabel>View My Courses</ng-template>
              </button>
            </div>
          </div>
        </div>

        <!-- Success/Error Messages -->
        <div *ngIf="successMessage && !enrolledCourse" class="toast toast-end toast-top">
          <div class="alert alert-success">
            <span>{{ successMessage }}</span>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .toast {
      z-index: 1000;
    }
    /* KAN-211: long course names (with or without spaces) must wrap inside the
       Successfully Enrolled card rather than spilling out of the green border. */
    .enrolled-course-name {
      overflow-wrap: anywhere;
      word-break: break-word;
    }
  `]
})
export class SimpleNotificationsComponent implements OnInit {
  invitations: any[] = [];
  organizationInvitations: any[] = [];
  // KAN-330: in-app notifications (e.g. instructor unit-completion alerts).
  notifications: AppNotification[] = [];
  notifPage = 1;
  notifTotal = 0;
  // KAN-331 Phase 10: the obligation-share row whose Pay CTA is mid-checkout
  // (guards double-submit while the redirect resolves).
  payingShareId: string | null = null;
  isLoading = false;
  // Pagination state (pagination migration P4) — one pager per list
  coursePage = 1;
  courseTotal = 0;
  orgPage = 1;
  orgTotal = 0;
  pageSize = 20;
  responding: string | null = null;
  successMessage = '';
  enrolledCourse: any = null; // Track successfully enrolled course
  enrolledCourseOrgId: string | null = null;
  enrolledCourseOrgName: string | null = null;
  isSwitching = false;
  private cdr = inject(ChangeDetectorRef);

  constructor(
    private http: HttpClient,
    private profileService: ProfileService,
    private authService: AuthService,
    private notificationStateService: NotificationStateService,
    private notificationsApi: NotificationsApiService,
    private paginationService: PaginationService,
    private paymentsCheckout: PaymentsCheckoutService
  ) {}

  ngOnInit(): void {
    this.loadInvitations();
  }

  loadInvitations(): void {
    this.isLoading = true;

    // Load both course and organization invitations in parallel (each list
    // pages independently — pagination migration P4)
    Promise.all([
      this.fetchCourseInvitations(),
      this.fetchOrgInvitations(),
      this.fetchNotifications()
    ]).finally(() => {
      this.isLoading = false;
      this.cdr.markForCheck();
    });
  }

  onNotifPageChange(page: number): void {
    this.notifPage = page;
    this.fetchNotifications().finally(() => this.cdr.markForCheck());
  }

  private fetchNotifications(): Promise<void> {
    return this.notificationsApi.getNotifications(this.notifPage, this.pageSize).toPromise()
      .then((parsed: any) => {
        this.notifications = parsed?.data ?? [];
        this.notifTotal = parsed?.total ?? 0;
        this.cdr.markForCheck();
      })
      .catch((error) => {
        console.error('Error loading notifications:', error);
        this.notifications = [];
        this.notifTotal = 0;
        this.cdr.markForCheck();
      });
  }

  markNotificationRead(notification: AppNotification): void {
    if (notification.isRead) return;
    this.notificationsApi.markRead(notification.id).subscribe({
      next: () => {
        notification.isRead = true;
        this.notificationStateService.refreshCount();
        this.cdr.markForCheck();
      },
      error: (err) => console.error('Error marking notification read:', err)
    });
  }

  markAllNotificationsRead(): void {
    this.notificationsApi.markAllRead().subscribe({
      next: () => {
        this.notifications = this.notifications.map(n => ({ ...n, isRead: true }));
        this.notificationStateService.refreshCount();
        this.cdr.markForCheck();
      },
      error: (err) => console.error('Error marking all notifications read:', err)
    });
  }

  get hasUnreadNotifications(): boolean {
    return this.notifications.some(n => !n.isRead);
  }

  /**
   * KAN-331 Phase 10 (task 10.3c2): "$X" label for a split-billing member share
   * notification, derived from the denormalized payload cents.
   */
  formatShareAmount(n: AppNotification): string {
    const cents = Number(n.payload?.shareCents ?? 0);
    return `$${formatCentsToDollars(Number.isFinite(cents) ? cents : 0)}`;
  }

  /**
   * KAN-331 Phase 11 (task 11.6c): a lapsed dues reminder (membership ended,
   * access paused) vs an upcoming-renewal nudge. The scanner writes reminderKind
   * = 'upcoming_7d' | 'upcoming_1d' | 'lapsed' on the denormalized payload.
   */
  isDuesReminderLapsed(n: AppNotification): boolean {
    return n.payload?.reminderKind === 'lapsed';
  }

  /**
   * KAN-331 Phase 11 (task 11.6c): the dues-reminder headline. Upcoming renewals
   * name the plan and (when present) the renewal date; a lapsed membership states
   * that access is paused. planName comes from the org's dues plan and is rendered
   * via interpolation (Angular auto-escapes) so it is XSS-safe.
   */
  duesReminderHeadline(n: AppNotification): string {
    const plan = n.payload?.planName || 'membership';
    if (this.isDuesReminderLapsed(n)) {
      return `Your ${plan} membership lapsed`;
    }
    const when = n.payload?.periodEndLabel ? ` on ${n.payload.periodEndLabel}` : '';
    return `Your ${plan} dues renew${when}`;
  }

  /** KAN-331 Phase 11 (task 11.6c): CTA label — "Resubscribe" once lapsed, else "View dues". */
  duesReminderCtaLabel(n: AppNotification): string {
    return this.isDuesReminderLapsed(n) ? 'Resubscribe' : 'View dues';
  }

  /**
   * KAN-331 Phase 10 (task 10.3c2): start hosted checkout for an obligation
   * share. This is the ONLY effect of the Pay CTA - fulfillment is webhook-only
   * and an unpaid share never gates the member's access (D15), so nothing here
   * locks, removes, or reads course access; it just resolves a session and
   * redirects. targetId is the share id (server resolves the price).
   */
  payObligationShare(n: AppNotification): void {
    const shareId = n.payload?.shareId;
    if (!shareId || this.payingShareId === shareId) return;

    this.payingShareId = shareId;
    this.cdr.markForCheck();

    this.paymentsCheckout.createObligationShareCheckoutSession(shareId).subscribe({
      next: (session) => {
        // Full-page redirect to Stripe's hosted checkout (shared spy seam).
        this.paymentsCheckout.redirectToHostedCheckout(session.url);
      },
      error: (err) => {
        this.payingShareId = null;
        this.successMessage = this.paymentsCheckout.checkoutErrorMessage(err);
        // Zoneless: this field is mutated off the CD cycle in an HTTP callback,
        // so force a synchronous render of the error toast.
        this.cdr.detectChanges();
        setTimeout(() => {
          this.successMessage = '';
          this.cdr.markForCheck();
        }, 5000);
      }
    });
  }

  onCoursePageChange(page: number): void {
    this.coursePage = page;
    this.fetchCourseInvitations();
  }

  onOrgPageChange(page: number): void {
    this.orgPage = page;
    this.fetchOrgInvitations();
  }

  private fetchCourseInvitations(): Promise<void> {
    const params = { page: String(this.coursePage), limit: String(this.pageSize) };

    return this.http.get(`${environment.apiUrl}/invitations/received`, { params }).toPromise()
      .then((response: any) => {
        // fromEnvelope handles both the pagination envelope and the legacy
        // {invitations: [...]} shape (single page).
        const parsed = this.paginationService.fromEnvelope<any>(response?.data ?? response);
        this.invitations = parsed.data;
        this.courseTotal = parsed.total;
        this.cdr.markForCheck();
      })
      .catch((error) => {
        console.error('Error loading course invitations:', error);
        this.invitations = [];
        this.courseTotal = 0;
        this.cdr.markForCheck();
      });
  }

  private fetchOrgInvitations(): Promise<void> {
    const params = { page: String(this.orgPage), limit: String(this.pageSize), status: 'pending' };

    return this.http.get(`${environment.apiUrl}/organization-invitations/received`, { params }).toPromise()
      .then((response: any) => {
        const parsed = this.paginationService.fromEnvelope<any>(response?.data ?? response);
        // Server filters to pending when paginated; keep the client-side filter
        // as defense-in-depth for legacy (non-envelope) responses.
        this.organizationInvitations = parsed.data.filter((inv: any) => inv.status === 'pending');
        this.orgTotal = parsed.total;
        this.cdr.markForCheck();
      })
      .catch((error) => {
        console.error('Error loading organization invitations:', error);
        this.organizationInvitations = [];
        this.orgTotal = 0;
        this.cdr.markForCheck();
      });
  }

  respondToInvitation(invitation: any, action: 'accept' | 'reject'): void {
    if (this.responding === invitation.id) return;

    this.responding = invitation.id;

    // Create a safe API call that won't be cancelled by navigation
    const request$ = this.http.put(`${environment.apiUrl}/invitations/${invitation.id}/respond`, {
      action: action
    });

    // Subscribe without component lifecycle dependency
    request$.subscribe({
      next: (response: any) => {
        console.log('Invitation response successful:', response);

        if (action === 'accept') {
          // Store enrolled course details for success display
          this.enrolledCourse = {
            name: invitation.courseName,
            thumbnail: invitation.courseThumbnail,
            description: invitation.courseDescription
          };
          this.successMessage = `Successfully enrolled in "${invitation.courseName}"!`;

          // KAN-119: capture the owning org from the accept response. We do NOT
          // auto-switch the org context (the earlier KAN-185 silent auto-switch
          // is intentionally removed) and we do NOT surface the course into the
          // current org's view. Instead the success card shows a clear notice
          // naming the owning org so the learner can switch deliberately. The org
          // name comes straight from the accept response; the org-list refresh
          // below only backfills it for older responses that omit the name.
          const respData = response?.data || response;
          this.enrolledCourseOrgId = respData?.organizationId || null;
          this.enrolledCourseOrgName = respData?.organizationName || null;

          // Refresh the user's organizations since they may have just been added
          // to a new org — this keeps the sidebar organization switcher in sync so
          // the new org is selectable. It does NOT change the active org context.
          this.authService.getUserOrganizations().subscribe({
            next: (orgResponse: any) => {
              const data = orgResponse.data || orgResponse;
              const user = this.profileService.getCurrentUser();
              if (user && data.organizations) {
                user.organizations = data.organizations;
                this.profileService.setCurrentUser(user);
                // Notify other components about organization changes
                this.authService.notifyOrganizationsUpdated(data.organizations);
              }
              // Fallback only: resolve the friendly name from the refreshed list
              // when the accept response did not already provide it.
              if (!this.enrolledCourseOrgName && this.enrolledCourseOrgId && Array.isArray(data?.organizations)) {
                const matched = data.organizations.find((o: any) => o.id === this.enrolledCourseOrgId);
                this.enrolledCourseOrgName = matched?.name || null;
              }
              // Zoneless (KAN-290): the cross-org notice text depends on this
              // async-set org name, so force a synchronous render — markForCheck
              // alone is not a reliable re-render trigger here.
              this.cdr.detectChanges();
            },
            error: (err) => console.error('Error refreshing organizations:', err)
          });
        } else {
          this.successMessage = 'Invitation declined.';
          // Clear message after delay for rejections
          setTimeout(() => {
            this.successMessage = '';
            this.cdr.markForCheck();
          }, 5000);
        }

        // Remove from list and refresh notification badge immediately
        this.invitations = this.invitations.filter(inv => inv.id !== invitation.id);
        this.notificationStateService.refreshCount();
        this.responding = null;
        // Zoneless (KAN-290 / KAN-119): render the enrollment success card and
        // the cross-org switch notice synchronously from this async HTTP callback.
        // These fields are set above in the same callback; markForCheck is not a
        // dependable re-render trigger for plain fields mutated off the CD cycle.
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error responding to invitation:', error);
        this.successMessage = 'Failed to process invitation. Please try again.';
        this.responding = null;
        this.cdr.markForCheck();

        // Clear error message after delay
        setTimeout(() => {
          this.successMessage = '';
          this.cdr.markForCheck();
        }, 5000);
      }
    });
  }

  needsOrgSwitch(): boolean {
    if (!this.enrolledCourseOrgId) return false;
    const currentOrgId = this.authService.getCurrentOrganizationId();
    return currentOrgId !== this.enrolledCourseOrgId;
  }

  async navigateToMyCourses(): Promise<void> {
    // KAN-119: the switch is deliberate and user-initiated (this button click),
    // never automatic. If the course we just enrolled in belongs to a different
    // org than the active one, persist the switch server-side first so /courses
    // loads in the right org context after the redirect.
    if (this.needsOrgSwitch() && this.enrolledCourseOrgId) {
      this.isSwitching = true;
      this.cdr.markForCheck();
      try {
        await this.authService.setDefaultOrganization(this.enrolledCourseOrgId).toPromise();
      } catch (err) {
        console.error('Failed to switch organization before redirect:', err);
        // Redirect anyway — user can switch manually via the sidebar.
      } finally {
        this.isSwitching = false;
      }
    }
    window.location.href = '/courses';
  }

  isAlreadyMemberOfOrg(invitation: any): boolean {
    const orgId = invitation?.organizationId;
    if (!orgId) return false;
    const user = this.profileService.getCurrentUser();
    const orgs = user?.organizations;
    if (!Array.isArray(orgs)) return false;
    return orgs.some((o: any) => o?.id === orgId);
  }

  getCourseInitials(courseName: string): string {
    if (!courseName) return '??';

    // Remove numbers and special characters, keep only letters and spaces
    const cleanName = courseName.replace(/[^a-zA-Z\s]/g, '').trim();

    if (!cleanName) return '??';

    // Split into words
    const words = cleanName.split(/\s+/).filter(word => word.length > 0);

    if (words.length === 0) return '??';

    if (words.length === 1) {
      // Single word: use first two letters
      const word = words[0].toUpperCase();
      return word.length >= 2 ? word.substring(0, 2) : word;
    } else {
      // Multiple words: use first letter of first word and first letter of last word
      const firstLetter = words[0][0].toUpperCase();
      const lastLetter = words[words.length - 1][0].toUpperCase();
      return firstLetter + lastLetter;
    }
  }

  respondToOrgInvitation(invitation: any, action: 'accept' | 'reject'): void {
    if (this.responding === invitation.id) return;

    this.responding = invitation.id;

    const request$ = this.http.put(`${environment.apiUrl}/organization-invitations/${invitation.id}/respond`, {
      action: action
    });

    request$.subscribe({
      next: (response: any) => {
        console.log('Organization invitation response successful:', response);

        if (action === 'accept') {
          this.successMessage = `Successfully joined "${invitation.organizationName}"!`;
          this.cdr.markForCheck();

          // Fetch fresh user organizations to update state
          this.authService.getUserOrganizations().subscribe({
            next: (response: any) => {
              console.log('Refreshed user organizations:', response);

              // Handle wrapped API response: {success: true, data: {...}}
              const orgData = response.data || response;

              // Get current user from cache and update
              const currentUser = this.authService.getCurrentUser();
              if (currentUser) {
                currentUser.organizations = orgData.organizations;
                currentUser.currentOrgId = orgData.currentOrgId;
                currentUser.currentOrgRole = orgData.currentOrgRole;

                // Save updated user to cache
                this.profileService.setCurrentUser(currentUser);
                console.log('Updated cached user with new organizations:', orgData.organizations?.length);

                // Notify all subscribers (like organization-switcher) that organizations have changed
                this.authService.notifyOrganizationsUpdated(orgData.organizations);
              }

              // Remove invitation from list and refresh notification badge
              this.organizationInvitations = this.organizationInvitations.filter(inv => inv.id !== invitation.id);
              this.notificationStateService.refreshCount();
              this.responding = null;
              this.cdr.markForCheck();

              // Clear success message after delay
              setTimeout(() => {
                this.successMessage = '';
                this.cdr.markForCheck();
              }, 5000);
            },
            error: (error) => {
              console.error('Error refreshing user organizations:', error);
              // Remove invitation from list even if refresh fails, and refresh badge
              this.organizationInvitations = this.organizationInvitations.filter(inv => inv.id !== invitation.id);
              this.notificationStateService.refreshCount();
              this.responding = null;
              this.cdr.markForCheck();
              // Clear message after delay
              setTimeout(() => {
                this.successMessage = '';
                this.cdr.markForCheck();
              }, 5000);
            }
          });
        } else {
          this.successMessage = 'Organization invitation declined.';

          // Remove from list and refresh notification badge
          this.organizationInvitations = this.organizationInvitations.filter(inv => inv.id !== invitation.id);
          this.notificationStateService.refreshCount();
          this.responding = null;
          this.cdr.markForCheck();

          // Clear message after delay
          setTimeout(() => {
            this.successMessage = '';
            this.cdr.markForCheck();
          }, 5000);
        }
      },
      error: (error) => {
        console.error('Error responding to organization invitation:', error);
        this.successMessage = 'Failed to process invitation. Please try again.';
        this.responding = null;
        this.cdr.markForCheck();

        setTimeout(() => {
          this.successMessage = '';
          this.cdr.markForCheck();
        }, 5000);
      }
    });
  }

  getRoleDisplay(role: string): string {
    if (!role) return 'Member';
    return role.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }
}
